import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';

import {
  CreateChartOfAccountSchema,
  accountTypeFromCode,
  type AccountType,
  type ChartOfAccount,
  type CreateChartOfAccount,
  type DimensionRequirement,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { AppModal } from '../components/AppModal';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import { buildGroupOptions, type GroupOptions } from '../lib/coa-groups';
import {
  useActiveCompany,
  useActiveCompanyId,
  useCoa,
  useCreateCoa,
  useDeleteCoa,
  useUpdateCoa,
} from '../lib/queries';

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

type TypeFilter = AccountType | 'all';

// ---- Account-type labels ---------------------------------------------------

const TYPE_LABELS: Record<AccountType, string> = {
  ASSET: 'สินทรัพย์',
  LIABILITY: 'หนี้สิน',
  EQUITY: 'ส่วนของผู้ถือหุ้น',
  REVENUE: 'รายได้',
  COST: 'ต้นทุน',
  EXPENSE: 'ค่าใช้จ่าย',
};

// Em dash placeholder for a blank group cell (pre-reseed dev data may carry
// null group columns; the real master populates all four).
const GROUP_DASH = '—';

// ---- Spreadsheet helpers (xlsx/csv import + xlsx export) -------------------

// Column order / header keys for the COA sheet. Mirrors the create/update
// schema so an exported file round-trips straight back through the importer.
const COA_CSV_COLS = [
  'code',
  'nameTh',
  'nameEn',
  'type',
  'level',
  'parentCode',
  'isControl',
  'isActive',
  'branchPolicy',
  'departmentPolicy',
] as const;

// Local YYYY-MM-DD for export filenames.
function todayStamp(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Human-readable spec for each CSV column — drives the import dialog's field
// reference table so an accountant doesn't have to guess what each header means.
const COA_CSV_FIELDS: {
  col: (typeof COA_CSV_COLS)[number];
  label: string;
  required: boolean;
  hint: string;
}[] = [
  {
    col: 'code',
    label: 'รหัสบัญชี',
    required: true,
    hint: 'เช่น 1111 (2–20 ตัวอักษร, ห้ามซ้ำในบริษัท)',
  },
  { col: 'nameTh', label: 'ชื่อบัญชี (ไทย)', required: true, hint: 'เช่น เงินสดในมือ' },
  {
    col: 'nameEn',
    label: 'ชื่อบัญชี (อังกฤษ)',
    required: false,
    hint: 'เว้นว่างได้ — จะใช้ชื่อไทยแทน',
  },
  {
    col: 'type',
    label: 'ประเภทบัญชี',
    required: true,
    hint: 'ASSET=สินทรัพย์, LIABILITY=หนี้สิน, EQUITY=ส่วนของผู้ถือหุ้น, REVENUE=รายได้, COST=ต้นทุน, EXPENSE=ค่าใช้จ่าย',
  },
  {
    col: 'level',
    label: 'ระดับ',
    required: true,
    hint: '0=หัวบัญชีรวม, 1=กลุ่ม, 2=หมวด, 3=บัญชีย่อย (0–6)',
  },
  {
    col: 'parentCode',
    label: 'รหัสบัญชีแม่',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง, ประเภทเดียวกัน และระดับสูงกว่า',
  },
  {
    col: 'isControl',
    label: 'บัญชีคุม',
    required: false,
    hint: 'true/false — true = ห้ามลงรายการตรง (ค่าเริ่มต้น false)',
  },
  { col: 'isActive', label: 'สถานะใช้งาน', required: false, hint: 'true/false (ค่าเริ่มต้น true)' },
  {
    col: 'branchPolicy',
    label: 'นโยบายสาขา',
    required: false,
    hint: 'NONE=ไม่ใช้, OPTIONAL=ไม่บังคับ, REQUIRED=บังคับ (ค่าเริ่มต้น OPTIONAL)',
  },
  {
    col: 'departmentPolicy',
    label: 'นโยบายหน่วยงาน / ศูนย์ต้นทุน',
    required: false,
    hint: 'NONE=ไม่ใช้, OPTIONAL=ไม่บังคับ, REQUIRED=บังคับ (ค่าเริ่มต้น OPTIONAL)',
  },
];

// Template = header + a few worked examples (parent → child hierarchy, plus a
// P&L account that requires a cost center). Users edit/replace these rows.
const COA_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  ['1000', 'สินทรัพย์', 'Assets', 'ASSET', 0, '', true, true, 'NONE', 'NONE'],
  ['1100', 'สินทรัพย์หมุนเวียน', 'Current Assets', 'ASSET', 1, '1000', true, true, 'NONE', 'NONE'],
  ['1115', 'เงินสดย่อย', 'Petty Cash', 'ASSET', 3, '1100', false, true, 'OPTIONAL', 'OPTIONAL'],
  ['5310', 'ค่าเดินทาง', 'Travel Expense', 'EXPENSE', 3, '', false, true, 'OPTIONAL', 'REQUIRED'],
];

// Comfortable column widths (in chars) for the exported worksheet.
const COA_COL_WIDTHS: { wch: number }[] = [
  { wch: 10 }, // code
  { wch: 34 }, // nameTh
  { wch: 30 }, // nameEn
  { wch: 12 }, // type
  { wch: 7 }, // level
  { wch: 12 }, // parentCode
  { wch: 10 }, // isControl
  { wch: 10 }, // isActive
  { wch: 14 }, // branchPolicy
  { wch: 16 }, // departmentPolicy
];

type CellValue = string | number | boolean;

function coaRowToArray(r: ChartOfAccount): CellValue[] {
  return [
    r.code,
    r.nameTh,
    r.nameEn,
    r.type,
    r.level,
    r.parentCode ?? '',
    r.isControl,
    r.isActive,
    r.branchPolicy,
    r.departmentPolicy,
  ];
}

// Build a single-sheet workbook from a header+rows grid and trigger a .xlsx
// download in the browser. The header row is the COA_CSV_COLS schema keys so
// an exported file round-trips straight back through the importer. SheetJS is
// loaded on demand (it's ~165 kB gzip) so it never weighs down the /coa page.
async function downloadXlsx(filename: string, aoa: CellValue[][]): Promise<void> {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = COA_COL_WIDTHS;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ผังบัญชี');
  XLSX.writeFile(wb, filename, { bookType: 'xlsx', compression: true });
}

// Cell → string. With raw:false SheetJS yields formatted primitives; guard the
// unknown so eslint's no-base-to-string stays happy and objects never leak in.
function cellToString(c: unknown): string {
  if (c === null || c === undefined) return '';
  if (typeof c === 'string') return c;
  if (typeof c === 'number' || typeof c === 'boolean') return String(c);
  return '';
}

// Read an uploaded spreadsheet (.xlsx / .xls / .csv) into a string grid that
// mapCoaCsv can validate. SheetJS auto-detects the format from the bytes.
async function fileToGrid(file: File): Promise<string[][]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer());
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  // header:1 → array-of-arrays; raw:false → formatted strings; defval keeps gaps.
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: '' });
  return grid.map((row) => row.map(cellToString));
}

function parseBool(v: string | undefined, fallback: boolean): boolean {
  const s = (v ?? '').trim().toLowerCase();
  if (s === '') return fallback;
  return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'ใช่';
}

interface ParsedCoaRow {
  rowNo: number;
  data: CreateChartOfAccount | null;
  error: string | null;
}

// Map a parsed CSV grid (with a header row) into validated COA create payloads.
function mapCoaCsv(grid: string[][]): { rows: ParsedCoaRow[]; headerError: string | null } {
  const header = grid[0];
  if (!header) return { rows: [], headerError: 'ไฟล์ว่างเปล่า' };
  const trimmed = header.map((h) => h.trim());
  const idx = (name: string) => trimmed.indexOf(name);
  for (const required of ['code', 'nameTh', 'type', 'level']) {
    if (idx(required) === -1) {
      return { rows: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${required}` };
    }
  }
  const cell = (cols: string[], name: string): string => {
    const i = idx(name);
    return i === -1 ? '' : (cols[i] ?? '').trim();
  };
  const out: ParsedCoaRow[] = [];
  for (let r = 1; r < grid.length; r++) {
    const cols = grid[r];
    // Skip fully blank lines.
    if (!cols || cols.every((c) => c.trim() === '')) continue;
    const rowNo = r + 1; // 1-based, accounts for the header row
    const nameTh = cell(cols, 'nameTh');
    const candidate = {
      code: cell(cols, 'code'),
      nameTh,
      nameEn: cell(cols, 'nameEn') || nameTh,
      type: cell(cols, 'type'),
      level: Number(cell(cols, 'level')),
      parentCode: cell(cols, 'parentCode') || null,
      isControl: parseBool(cell(cols, 'isControl'), false),
      isActive: parseBool(cell(cols, 'isActive'), true),
      branchPolicy: cell(cols, 'branchPolicy') || undefined,
      departmentPolicy: cell(cols, 'departmentPolicy') || undefined,
    };
    const parsed = CreateChartOfAccountSchema.safeParse(candidate);
    if (parsed.success) {
      out.push({ rowNo, data: parsed.data, error: null });
    } else {
      const first = parsed.error.issues[0];
      out.push({
        rowNo,
        data: null,
        error: first ? `${first.path.join('.')}: ${first.message}` : 'ข้อมูลไม่ถูกต้อง',
      });
    }
  }
  return { rows: out, headerError: null };
}

export const Route = createFileRoute('/coa')({
  component: CoaPage,
});

function CoaPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  // activeOnly=false so we show every seeded row, including header/inactive nodes.
  const { data: rows = [], isPending, error } = useCoa(companyId, false);

  const [q, setQ] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [editTarget, setEditTarget] = useState<ChartOfAccount | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const deleteCoa = useDeleteCoa(companyId);

  async function handleExport() {
    // Export every row (including header/inactive) so the file round-trips
    // straight back through นำเข้า. Columns mirror the create/update schema.
    // Filename carries the company code + export date, e.g. ผังบัญชี_DCC_2026-05-29.xlsx
    const code = company?.code ?? 'company';
    const sorted = [...rows].sort((a, b) => a.code.localeCompare(b.code));
    const aoa: CellValue[][] = [[...COA_CSV_COLS], ...sorted.map(coaRowToArray)];
    await downloadXlsx(`ผังบัญชี_${code}_${todayStamp()}.xlsx`, aoa);
  }

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (type !== 'all' && r.type !== type) return false;
        if (q && !(r.code.includes(q) || r.nameTh.toLowerCase().includes(q.toLowerCase())))
          return false;
        return true;
      }),
    [rows, type, q],
  );

  // Flat table (spec 43): the real master COA exposes its hierarchy as four text
  // columns (majorGroup → midGroup → subGroup → fsLine) rather than a parentCode
  // tree. Each account is one row carrying those columns directly — mirroring the
  // master-data spreadsheet — sorted by code so same-group rows stay contiguous.
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => a.code.localeCompare(b.code)),
    [filtered],
  );

  // Option lists for the add/edit modal's cascading group fields. Derived from
  // the full row set already loaded — no extra API call.
  const groupOptions = useMemo(() => buildGroupOptions(rows), [rows]);

  // KPI cards roll up by type — count of accounts per type, since per-account
  // balance + movement aren't exposed by the COA endpoint (they're derived
  // from journal_lines aggregation; see /trial-balance or /ledger).
  const countByType = useMemo(() => {
    const out: Partial<Record<AccountType, number>> = {};
    for (const r of rows) {
      out[r.type] = (out[r.type] ?? 0) + 1;
    }
    return out;
  }, [rows]);

  const kpis: { k: string; v: number; c: string }[] = [
    { k: 'สินทรัพย์', v: countByType.ASSET ?? 0, c: '#1a56a6' },
    { k: 'หนี้สิน', v: countByType.LIABILITY ?? 0, c: '#b54708' },
    { k: 'ส่วนของผู้ถือหุ้น', v: countByType.EQUITY ?? 0, c: '#7030A0' },
    { k: 'รายได้', v: countByType.REVENUE ?? 0, c: '#117a3d' },
    { k: 'ต้นทุน', v: countByType.COST ?? 0, c: '#9a3412' },
    { k: 'ค่าใช้จ่าย', v: countByType.EXPENSE ?? 0, c: '#b42318' },
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'ผังบัญชี']}
        title="ผังบัญชี (Chart of Accounts)"
        actions={
          <>
            <HelpButton guide="coa" />
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={rows.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)} disabled={!companyId}>
              <Icon name="plus" size={14} /> เพิ่มบัญชี
            </button>
          </>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6,1fr)',
          gap: 10,
          marginBottom: 'var(--section-gap)',
        }}
      >
        {kpis.map((b) => (
          <div className="kpi" key={b.k} style={{ padding: '10px 12px' }}>
            <div className="label">
              <span className="dot" style={{ background: b.c }} />
              {b.k}
            </div>
            <div className="value tnum" style={{ fontSize: 20 }}>
              {b.v} <span className="unit">บัญชี</span>
            </div>
          </div>
        ))}
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหารหัสบัญชีหรือชื่อบัญชี"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 28,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '8px center',
            }}
          />
          <select
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value as TypeFilter)}
          >
            <option value="all">ประเภท: ทั้งหมด</option>
            <option value="ASSET">สินทรัพย์</option>
            <option value="LIABILITY">หนี้สิน</option>
            <option value="EQUITY">ส่วนของผู้ถือหุ้น</option>
            <option value="REVENUE">รายได้</option>
            <option value="COST">ต้นทุน</option>
            <option value="EXPENSE">ค่าใช้จ่าย</option>
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                setQ('');
                setType('all');
              }}
            >
              ล้างการค้นหา
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 100 }}>รหัสบัญชี</th>
                <th style={{ minWidth: 180 }}>ชื่อบัญชี</th>
                <th style={{ width: 96 }}>ประเภท</th>
                <th style={{ minWidth: 130 }}>กลุ่มใหญ่</th>
                <th style={{ minWidth: 130 }}>กลุ่มกลาง</th>
                <th style={{ minWidth: 130 }}>กลุ่มย่อย</th>
                <th style={{ minWidth: 150 }}>กลุ่มในงบการเงิน</th>
                <th style={{ width: 80 }}>สาขา</th>
                <th style={{ width: 120 }}>หน่วยงาน / ศูนย์ต้นทุน</th>
                <th style={{ width: 84 }}>สถานะ</th>
                <th style={{ width: 96 }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={11} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={11}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {error.message}
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={11} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ไม่พบบัญชีที่ตรงกับเงื่อนไข
                  </td>
                </tr>
              ) : (
                sorted.map((r) => (
                  <tr key={r.code}>
                    <td className="code">{r.code}</td>
                    <td>{r.nameTh}</td>
                    <td className="muted">{TYPE_LABELS[r.type]}</td>
                    <td className="muted" style={{ whiteSpace: 'normal' }}>
                      {r.majorGroup ?? <span style={{ opacity: 0.5 }}>{GROUP_DASH}</span>}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'normal' }}>
                      {r.midGroup ?? <span style={{ opacity: 0.5 }}>{GROUP_DASH}</span>}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'normal' }}>
                      {r.subGroup ?? <span style={{ opacity: 0.5 }}>{GROUP_DASH}</span>}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'normal' }}>
                      {r.fsLine ?? <span style={{ opacity: 0.5 }}>{GROUP_DASH}</span>}
                    </td>
                    <td>
                      <PolicyBadge policy={r.branchPolicy} />
                    </td>
                    <td>
                      <PolicyBadge policy={r.departmentPolicy} />
                    </td>
                    <td>
                      {r.isActive ? (
                        <span className="badge positive">
                          <span className="bdot" />
                          ใช้งาน
                        </span>
                      ) : (
                        <span className="badge neutral">ปิด</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn sm ghost"
                          onClick={() => setEditTarget(r)}
                          title="แก้ไข"
                          style={{ fontSize: 11 }}
                        >
                          แก้ไข
                        </button>
                        <button
                          className="btn sm ghost"
                          onClick={() => {
                            if (!r.id) return;
                            if (confirm(`ปิดใช้งานบัญชี ${r.code} ${r.nameTh}?`)) {
                              deleteCoa.mutate(r.id);
                            }
                          }}
                          title="ปิดใช้งาน"
                          disabled={!r.isActive || !r.id}
                          style={{ color: 'var(--negative)' }}
                        >
                          <Icon name="x" size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            แสดง {filtered.length} จาก {rows.length} บัญชี
          </span>
        </div>
      </div>

      {showImport && companyId && (
        <CoaImportModal companyId={companyId} rows={rows} onClose={() => setShowImport(false)} />
      )}
      {showAdd && companyId && (
        <CoaFormModal
          mode="add"
          companyId={companyId}
          groups={groupOptions}
          onClose={() => setShowAdd(false)}
        />
      )}
      {editTarget && companyId && (
        <CoaFormModal
          mode="edit"
          companyId={companyId}
          initial={editTarget}
          groups={groupOptions}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

// ---- Add/Edit modal ---------------------------------------------------------

interface CoaFormModalProps {
  mode: 'add' | 'edit';
  companyId: string;
  initial?: ChartOfAccount;
  groups: GroupOptions;
  onClose: () => void;
}

const POLICY_LABELS: Record<DimensionRequirement, string> = {
  NONE: 'ไม่ใช้',
  OPTIONAL: 'ไม่บังคับ',
  REQUIRED: 'บังคับ',
};

const POLICY_OPTIONS = ['NONE', 'OPTIONAL', 'REQUIRED'] as const;

// Compact per-row badge showing the dimension policy. REQUIRED stands out
// (warning), OPTIONAL is neutral, NONE is muted/quiet.
function PolicyBadge({ policy }: { policy: DimensionRequirement }) {
  if (policy === 'REQUIRED') {
    return <span className="badge warning">{POLICY_LABELS.REQUIRED}</span>;
  }
  if (policy === 'NONE') {
    return (
      <span className="muted" style={{ fontSize: 12 }}>
        {POLICY_LABELS.NONE}
      </span>
    );
  }
  return <span className="badge neutral">{POLICY_LABELS.OPTIONAL}</span>;
}

function CoaFormModal({ mode, companyId, initial, groups, onClose }: CoaFormModalProps) {
  const createCoa = useCreateCoa(companyId);
  const updateCoa = useUpdateCoa(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [nameTh, setNameTh] = useState(initial?.nameTh ?? '');
  const [nameEn, setNameEn] = useState(initial?.nameEn ?? '');
  const [majorGroup, setMajorGroup] = useState(initial?.majorGroup ?? '');
  const [midGroup, setMidGroup] = useState(initial?.midGroup ?? '');
  const [subGroup, setSubGroup] = useState(initial?.subGroup ?? '');
  const [fsLine, setFsLine] = useState(initial?.fsLine ?? '');
  const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true);
  const [branchPolicy, setBranchPolicy] = useState<DimensionRequirement>(
    initial?.branchPolicy ?? 'OPTIONAL',
  );
  const [departmentPolicy, setDepartmentPolicy] = useState<DimensionRequirement>(
    initial?.departmentPolicy ?? 'OPTIONAL',
  );
  const [err, setErr] = useState<string | null>(null);

  const isEdit = mode === 'edit';
  const mutation = isEdit ? updateCoa : createCoa;

  // Type is implied by the code's first digit (1=ASSET … 6=EXPENSE). Show it
  // read-only; a partial/invalid code yields an em dash instead of throwing.
  const typeLabel = useMemo(() => {
    try {
      return TYPE_LABELS[accountTypeFromCode(code)];
    } catch {
      return '—';
    }
  }, [code]);

  // Strict cascade: each level's options are scoped to the chosen parent, and
  // changing a parent resets its descendants. fsLine is 1:1 with subGroup in the
  // master, so it auto-fills when the chosen sub has a single fsLine.
  const midOptions = majorGroup ? (groups.midsByMajor.get(majorGroup) ?? []) : [];
  const subOptions = midGroup ? (groups.subsByMid.get(midGroup) ?? []) : [];
  const fsOptions = subGroup ? (groups.fsLinesBySub.get(subGroup) ?? []) : [];

  function pickMajor(v: string) {
    setMajorGroup(v);
    setMidGroup('');
    setSubGroup('');
    setFsLine('');
  }
  function pickMid(v: string) {
    setMidGroup(v);
    setSubGroup('');
    setFsLine('');
  }
  function pickSub(v: string) {
    setSubGroup(v);
    const fs = groups.fsLinesBySub.get(v);
    setFsLine(fs?.length === 1 ? (fs[0] ?? '') : '');
  }

  const orNull = (s: string) => {
    const t = s.trim();
    return t === '' ? null : t;
  };

  // Real-COA accounts (spec 43) are postable 8-digit leaves: type is derived
  // from the code's first digit and level is fixed at 3. The four group columns
  // (majorGroup/midGroup/subGroup/fsLine) carry the statement hierarchy and are
  // edited here directly.
  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    try {
      if (isEdit && initial?.id) {
        // code + type are immutable on edit; omit them.
        await updateCoa.mutateAsync({
          id: initial.id,
          data: {
            nameTh: nameTh.trim(),
            nameEn: nameEn.trim() || nameTh.trim(),
            isControl: false,
            isActive,
            branchPolicy,
            departmentPolicy,
            majorGroup: orNull(majorGroup),
            midGroup: orNull(midGroup),
            subGroup: orNull(subGroup),
            fsLine: orNull(fsLine),
          },
        });
      } else {
        const data: CreateChartOfAccount = {
          code: code.trim(),
          nameTh: nameTh.trim(),
          nameEn: nameEn.trim() || nameTh.trim(),
          // type is required by the schema; derive it from the code's first
          // digit. Throws on an out-of-range code → caught below as an error.
          type: accountTypeFromCode(code.trim()),
          level: 3,
          parentCode: null,
          isControl: false,
          isActive,
          branchPolicy,
          departmentPolicy,
          majorGroup: orNull(majorGroup),
          midGroup: orNull(midGroup),
          subGroup: orNull(subGroup),
          fsLine: orNull(fsLine),
        };
        await createCoa.mutateAsync(data);
      }
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  const labelMuted: CSSProperties = { fontSize: 13, marginBottom: 4 };
  const cell: CSSProperties = { display: 'block' };

  return (
    <AppModal
      title={isEdit ? 'แก้ไขบัญชี' : 'เพิ่มบัญชีใหม่'}
      subtitle={
        isEdit
          ? `${initial?.code ?? ''} · ${initial?.nameTh ?? ''}`
          : 'ผังบัญชี 8 หลัก · ประเภทบัญชีอิงเลขหลักแรกของรหัสโดยอัตโนมัติ'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="submit"
            form="coa-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'กำลังบันทึก…' : isEdit ? 'บันทึกการแก้ไข' : '+ เพิ่มบัญชี'}
          </button>
        </>
      }
    >
      <form
        id="coa-form"
        onSubmit={(e) => void handleSubmit(e)}
        style={{ display: 'grid', gap: 14 }}
      >
        {/* Row 1: code · type (read-only) · nameTh · nameEn */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              รหัสบัญชี *
            </div>
            <input
              className="input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={isEdit}
              style={{ width: '100%' }}
              placeholder="เช่น 10110009"
            />
            {isEdit && (
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                รหัสแก้ไม่ได้ (linked กับ journal_lines)
              </div>
            )}
          </label>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              ประเภท (อิงรหัส)
            </div>
            <input
              className="input"
              value={typeLabel}
              disabled
              readOnly
              style={{ width: '100%' }}
            />
          </label>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              ชื่อบัญชี (ไทย) *
            </div>
            <input
              className="input"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              required
              style={{ width: '100%' }}
              placeholder="เช่น เงินสดย่อย - คลังสินค้า"
            />
          </label>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              ชื่อบัญชี (อังกฤษ)
            </div>
            <input
              className="input"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              style={{ width: '100%' }}
              placeholder="ว่างได้ — ใช้ชื่อไทยแทน"
            />
          </label>
        </div>

        {/* Group card: cascading datalists (pick existing or type new) */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '12px 14px',
            background: '#faf7fe',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#7a3fa8' }}>
            การจัดกลุ่มบัญชี (สำหรับงบการเงิน){' '}
            <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
              — เลือกจากรายการ หรือพิมพ์ค่าใหม่ได้
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            <label style={cell}>
              <div className="muted" style={labelMuted}>
                กลุ่มใหญ่
              </div>
              <SearchSelect
                value={majorGroup}
                onChange={pickMajor}
                options={groups.majors}
                placeholder="เลือกหรือพิมพ์"
              />
            </label>
            <label style={cell}>
              <div className="muted" style={labelMuted}>
                กลุ่มกลาง
              </div>
              <SearchSelect
                value={midGroup}
                onChange={pickMid}
                options={midOptions}
                disabled={!majorGroup}
                placeholder={majorGroup ? 'เลือกหรือพิมพ์' : 'เลือกกลุ่มใหญ่ก่อน'}
              />
            </label>
            <label style={cell}>
              <div className="muted" style={labelMuted}>
                กลุ่มย่อย
              </div>
              <SearchSelect
                value={subGroup}
                onChange={pickSub}
                options={subOptions}
                disabled={!midGroup}
                placeholder={midGroup ? 'เลือกหรือพิมพ์' : 'เลือกกลุ่มกลางก่อน'}
              />
            </label>
            <label style={cell}>
              <div className="muted" style={labelMuted}>
                กลุ่มในงบการเงิน
              </div>
              <SearchSelect
                value={fsLine}
                onChange={setFsLine}
                options={fsOptions}
                disabled={!subGroup}
                placeholder={subGroup ? 'เลือกหรือพิมพ์' : 'เลือกกลุ่มย่อยก่อน'}
              />
            </label>
          </div>
        </div>

        {/* Row 3: branch policy · department policy · active */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              นโยบายสาขา
            </div>
            <select
              className="select"
              value={branchPolicy}
              onChange={(e) => setBranchPolicy(e.target.value as DimensionRequirement)}
              style={{ width: '100%' }}
            >
              {POLICY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {POLICY_LABELS[p]}
                </option>
              ))}
            </select>
          </label>
          <label style={cell}>
            <div className="muted" style={labelMuted}>
              นโยบายหน่วยงาน / ศูนย์ต้นทุน
            </div>
            <select
              className="select"
              value={departmentPolicy}
              onChange={(e) => setDepartmentPolicy(e.target.value as DimensionRequirement)}
              style={{ width: '100%' }}
            >
              {POLICY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {POLICY_LABELS[p]}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 22 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span style={{ fontSize: 14 }}>ใช้งาน</span>
          </label>
        </div>

        {err && (
          <div
            style={{
              background: 'var(--negative-bg)',
              color: 'var(--negative)',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {err}
          </div>
        )}
      </form>
    </AppModal>
  );
}

// ---- Import modal -----------------------------------------------------------

interface CoaImportModalProps {
  companyId: string;
  rows: readonly ChartOfAccount[];
  onClose: () => void;
}

type ImportStage = 'pick' | 'preview' | 'running' | 'done';

interface ImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: { code: string; message: string }[];
}

function CoaImportModal({ companyId, rows, onClose }: CoaImportModalProps) {
  const createCoa = useCreateCoa(companyId);
  const updateCoa = useUpdateCoa(companyId);
  const fileRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<ImportStage>('pick');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCoaRow[]>([]);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ImportResult | null>(null);

  // Existing codes → row, for the create-vs-update decision and to fetch the id
  // needed by PATCH.
  const byCode = useMemo(() => {
    const m = new Map<string, ChartOfAccount>();
    for (const r of rows) m.set(r.code, r);
    return m;
  }, [rows]);

  const valid = parsed.filter((p): p is ParsedCoaRow & { data: CreateChartOfAccount } => !!p.data);
  const invalid = parsed.filter((p) => !p.data);
  const toUpdate = valid.filter((p) => byCode.has(p.data.code));
  const toCreate = valid.filter((p) => !byCode.has(p.data.code));

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const { rows: mapped, headerError: hErr } = mapCoaCsv(await fileToGrid(file));
    setHeaderError(hErr);
    setParsed(mapped);
    setStage('preview');
  }

  async function runImport() {
    setStage('running');
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    // Create parents before children — the API rejects a child whose parentCode
    // doesn't yet exist. Sorting by level ascending guarantees the ordering even
    // when a brand-new parent ships in the same file.
    const ordered = [...valid].sort(
      (a, b) => a.data.level - b.data.level || a.data.code.localeCompare(b.data.code),
    );
    setProgress({ done: 0, total: ordered.length });
    for (let i = 0; i < ordered.length; i++) {
      const item = ordered[i];
      if (!item) continue;
      const { data } = item;
      try {
        const existing = byCode.get(data.code);
        if (existing?.id) {
          await updateCoa.mutateAsync({
            id: existing.id,
            data: {
              nameTh: data.nameTh,
              nameEn: data.nameEn,
              type: data.type,
              level: data.level,
              parentCode: data.parentCode ?? null,
              isControl: data.isControl,
              isActive: data.isActive,
              branchPolicy: data.branchPolicy,
              departmentPolicy: data.departmentPolicy,
            },
          });
          res.updated++;
        } else {
          await createCoa.mutateAsync(data);
          res.created++;
        }
      } catch (err: unknown) {
        res.failed++;
        res.errors.push({
          code: data.code,
          message: err instanceof Error ? err.message : 'เกิดข้อผิดพลาด',
        });
      }
      setProgress({ done: i + 1, total: ordered.length });
    }
    setResult(res);
    setStage('done');
  }

  const stat: CSSProperties = { fontSize: 13, color: 'var(--muted)' };

  const footer =
    stage === 'done' ? (
      <button className="btn primary" onClick={onClose}>
        เสร็จสิ้น
      </button>
    ) : (
      <>
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          disabled={stage === 'running'}
        >
          ยกเลิก
        </button>
        {stage === 'preview' && !headerError && (
          <button
            className="btn primary"
            onClick={() => void runImport()}
            disabled={valid.length === 0}
          >
            นำเข้า {valid.length} รายการ
          </button>
        )}
      </>
    );

  return (
    <AppModal
      title="นำเข้าผังบัญชี (CSV)"
      onClose={stage === 'running' ? () => undefined : onClose}
      footer={footer}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        {stage === 'pick' && (
          <>
            <div style={stat}>
              เลือกไฟล์ Excel (.xlsx) หรือ CSV ที่มีหัวคอลัมน์ตามตารางด้านล่าง — รหัสบัญชีที่
              <b>มีอยู่แล้วจะถูกอัปเดต</b>, รหัสใหม่จะถูก<b>สร้าง</b>. แนะนำให้กด “ดาวน์โหลดเทมเพลต”
              หรือ “ส่งออก” แล้วแก้ในไฟล์นั้น
            </div>
            <div
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                overflowX: 'hidden',
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            >
              <table
                className="tbl"
                style={{ fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>คอลัมน์</th>
                    <th style={{ width: 150 }}>ความหมาย</th>
                    <th style={{ width: 80 }}>จำเป็น</th>
                    <th>ค่าที่ใช้ได้ / ตัวอย่าง</th>
                  </tr>
                </thead>
                <tbody>
                  {COA_CSV_FIELDS.map((f) => (
                    <tr key={f.col}>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <code style={{ fontSize: 12 }}>{f.col}</code>
                      </td>
                      <td style={{ whiteSpace: 'normal' }}>{f.label}</td>
                      <td>
                        {f.required ? (
                          <span className="badge warning">จำเป็น</span>
                        ) : (
                          <span className="muted">ไม่บังคับ</span>
                        )}
                      </td>
                      <td className="muted" style={{ whiteSpace: 'normal' }}>
                        {f.hint}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => void handleFile(e)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary" onClick={() => fileRef.current?.click()}>
                <Icon name="upload" size={14} /> เลือกไฟล์
              </button>
              <button
                className="btn"
                onClick={() =>
                  void downloadXlsx('coa_template.xlsx', [
                    [...COA_CSV_COLS],
                    ...COA_TEMPLATE_EXAMPLES,
                  ])
                }
              >
                <Icon name="download" size={14} /> ดาวน์โหลดเทมเพลต
              </button>
            </div>
          </>
        )}

        {stage === 'preview' &&
          (headerError ? (
            <div
              style={{
                background: 'var(--negative-bg)',
                color: 'var(--negative)',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {headerError}
            </div>
          ) : (
            <>
              <div style={stat}>
                ไฟล์: <b>{fileName}</b> — พบ {parsed.length} แถว
              </div>
              <div style={{ display: 'flex', gap: 18, fontSize: 14 }}>
                <span>
                  สร้างใหม่ <b style={{ color: 'var(--positive)' }}>{toCreate.length}</b>
                </span>
                <span>
                  อัปเดต <b style={{ color: '#1a56a6' }}>{toUpdate.length}</b>
                </span>
                <span>
                  ผิดพลาด <b style={{ color: 'var(--negative)' }}>{invalid.length}</b>
                </span>
              </div>
              {valid.length > 0 && (
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                  }}
                >
                  <table
                    className="tbl"
                    style={{ fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>รหัส</th>
                        <th>ชื่อบัญชี</th>
                        <th style={{ width: 90 }}>ประเภท</th>
                        <th style={{ width: 90 }}>การกระทำ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {valid.map((p) => (
                        <tr key={p.rowNo}>
                          <td className="code">{p.data.code}</td>
                          <td style={{ whiteSpace: 'normal' }}>{p.data.nameTh}</td>
                          <td className="muted">{TYPE_LABELS[p.data.type]}</td>
                          <td>
                            {byCode.has(p.data.code) ? (
                              <span className="badge neutral">อัปเดต</span>
                            ) : (
                              <span className="badge positive">สร้างใหม่</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {invalid.length > 0 && (
                <div
                  style={{
                    maxHeight: 160,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    wordBreak: 'break-word',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '6px 10px',
                    fontSize: 12.5,
                  }}
                >
                  {invalid.map((p) => (
                    <div key={p.rowNo} style={{ color: 'var(--negative)' }}>
                      แถว {p.rowNo}: {p.error}
                    </div>
                  ))}
                </div>
              )}
            </>
          ))}

        {stage === 'running' && (
          <div style={{ fontSize: 14 }}>
            กำลังนำเข้า… {progress.done}/{progress.total}
          </div>
        )}

        {stage === 'done' && result && (
          <>
            <div style={{ display: 'flex', gap: 18, fontSize: 14 }}>
              <span>
                สร้าง <b style={{ color: 'var(--positive)' }}>{result.created}</b>
              </span>
              <span>
                อัปเดต <b style={{ color: '#1a56a6' }}>{result.updated}</b>
              </span>
              <span>
                ล้มเหลว <b style={{ color: 'var(--negative)' }}>{result.failed}</b>
              </span>
            </div>
            {result.errors.length > 0 && (
              <div
                style={{
                  maxHeight: 160,
                  overflow: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: 12.5,
                }}
              >
                {result.errors.map((e) => (
                  <div key={e.code} style={{ color: 'var(--negative)' }}>
                    {e.code}: {e.message}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppModal>
  );
}
